import type { ChainPointV4 } from '../protocol/BinaryDecoderV4'

export class ChainStoreV4 {
  readonly positions: Float32Array
  readonly counts: Int32Array

  private readonly pathCapacity: number
  private readonly pathX: Float32Array
  private readonly pathZ: Float32Array
  private readonly pathD: Float32Array
  private readonly pathHead: Int32Array
  private readonly pathCount: Int32Array

  constructor(
    private readonly maxSnakes: number,
    readonly maxSegmentsPerSnake: number,
  ) {
    this.positions = new Float32Array(maxSnakes * maxSegmentsPerSnake * 2)
    this.counts = new Int32Array(maxSnakes)

    this.pathCapacity = maxSegmentsPerSnake * 8
    this.pathX = new Float32Array(maxSnakes * this.pathCapacity)
    this.pathZ = new Float32Array(maxSnakes * this.pathCapacity)
    this.pathD = new Float32Array(maxSnakes * this.pathCapacity)
    this.pathHead = new Int32Array(maxSnakes)
    this.pathCount = new Int32Array(maxSnakes)
  }

  private positionOffset(entityId: number, segmentIndex: number): number {
    return (entityId * this.maxSegmentsPerSnake + segmentIndex) * 2
  }

  private pathOffset(entityId: number, pathIndex: number): number {
    return entityId * this.pathCapacity + pathIndex
  }

  initSnake(entityId: number, headX: number, headZ: number, length: number, bearing: number, spacing: number): void {
    const count = Math.max(1, Math.min(Math.floor(length), this.maxSegmentsPerSnake))
    this.counts[entityId] = count

    const tailDx = -Math.cos(bearing)
    const tailDz = -Math.sin(bearing)
    for (let i = 0; i < count; i++) {
      const offset = this.positionOffset(entityId, i)
      this.positions[offset] = Math.fround(headX + tailDx * spacing * i)
      this.positions[offset + 1] = Math.fround(headZ + tailDz * spacing * i)
    }

    const pathOffset = this.pathOffset(entityId, 0)
    this.pathX[pathOffset] = Math.fround(headX)
    this.pathZ[pathOffset] = Math.fround(headZ)
    this.pathD[pathOffset] = 0
    this.pathHead[entityId] = 0
    this.pathCount[entityId] = 1
  }

  loadSnapshot(entityId: number, points: readonly ChainPointV4[]): void {
    const count = Math.max(0, Math.min(points.length, this.maxSegmentsPerSnake))
    this.counts[entityId] = count

    for (let i = 0; i < count; i++) {
      const offset = this.positionOffset(entityId, i)
      this.positions[offset] = Math.fround(points[i].x)
      this.positions[offset + 1] = Math.fround(points[i].z)
    }

    this.pathHead[entityId] = 0
    this.pathCount[entityId] = 0
    for (let i = count - 1; i >= 0; i--) {
      const writeIndex = count - 1 - i
      const offset = this.pathOffset(entityId, writeIndex)
      const point = points[i]
      this.pathX[offset] = Math.fround(point.x)
      this.pathZ[offset] = Math.fround(point.z)
      if (i === count - 1) {
        this.pathD[offset] = 0
      } else {
        const next = points[i + 1]
        this.pathD[offset] = Math.fround(Math.hypot(next.x - point.x, next.z - point.z))
      }
      this.pathCount[entityId]++
      this.pathHead[entityId] = writeIndex
    }
  }

  resetPathFromPositions(entityId: number): void {
    const count = Math.max(0, Math.min(this.counts[entityId], this.maxSegmentsPerSnake))
    this.pathHead[entityId] = 0
    this.pathCount[entityId] = 0
    if (count <= 0) return

    for (let segmentIndex = count - 1; segmentIndex >= 0; segmentIndex--) {
      const writeIndex = count - 1 - segmentIndex
      const pathOffset = this.pathOffset(entityId, writeIndex)
      const positionOffset = this.positionOffset(entityId, segmentIndex)
      const x = this.positions[positionOffset]
      const z = this.positions[positionOffset + 1]
      this.pathX[pathOffset] = x
      this.pathZ[pathOffset] = z
      if (segmentIndex === count - 1) {
        this.pathD[pathOffset] = 0
      } else {
        const nextPositionOffset = this.positionOffset(entityId, segmentIndex + 1)
        const nextX = this.positions[nextPositionOffset]
        const nextZ = this.positions[nextPositionOffset + 1]
        this.pathD[pathOffset] = Math.fround(Math.hypot(nextX - x, nextZ - z))
      }
      this.pathCount[entityId]++
      this.pathHead[entityId] = writeIndex
    }
  }

  updateChain(entityId: number, headX: number, headZ: number, spacing: number): void {
    const count = this.counts[entityId]
    if (count <= 0) return

    const headIndex = this.pathHead[entityId]
    const headOffset = this.pathOffset(entityId, headIndex)
    const prevHeadX = this.pathX[headOffset]
    const prevHeadZ = this.pathZ[headOffset]
    const moveDx = headX - prevHeadX
    const moveDz = headZ - prevHeadZ
    const moveDistance = Math.sqrt(moveDx * moveDx + moveDz * moveDz)

    if (moveDistance > 0.001) {
      const nextIndex = (headIndex + 1) % this.pathCapacity
      const nextOffset = this.pathOffset(entityId, nextIndex)
      this.pathX[nextOffset] = Math.fround(headX)
      this.pathZ[nextOffset] = Math.fround(headZ)
      this.pathD[nextOffset] = Math.fround(moveDistance)
      this.pathHead[entityId] = nextIndex
      this.pathCount[entityId] = Math.min(this.pathCount[entityId] + 1, this.pathCapacity)
    }

    const headPositionOffset = this.positionOffset(entityId, 0)
    this.positions[headPositionOffset] = Math.fround(headX)
    this.positions[headPositionOffset + 1] = Math.fround(headZ)

    if (count === 1) return

    const availablePoints = this.pathCount[entityId]
    if (availablePoints <= 1) {
      for (let segmentIndex = 1; segmentIndex < count; segmentIndex++) {
        const offset = this.positionOffset(entityId, segmentIndex)
        this.positions[offset] = Math.fround(headX)
        this.positions[offset + 1] = Math.fround(headZ)
      }
      return
    }

    let segmentIndex = 0
    let distanceFromHead = 0
    let currentPathIndex = this.pathHead[entityId]
    let steps = 0

    while (segmentIndex < count - 1 && steps < availablePoints - 1) {
      const previousPathIndex = (currentPathIndex - 1 + this.pathCapacity) % this.pathCapacity
      const nextOffset = this.pathOffset(entityId, currentPathIndex)
      const currentOffset = this.pathOffset(entityId, previousPathIndex)
      const stepDistance = this.pathD[nextOffset]
      const nextX = this.pathX[nextOffset]
      const nextZ = this.pathZ[nextOffset]
      const currentX = this.pathX[currentOffset]
      const currentZ = this.pathZ[currentOffset]
      const nextDistanceFromHead = distanceFromHead + stepDistance

      while (segmentIndex < count - 1 && (segmentIndex + 1) * spacing <= nextDistanceFromHead) {
        const targetDistance = (segmentIndex + 1) * spacing
        const t = stepDistance > 0 ? (targetDistance - distanceFromHead) / stepDistance : 0
        const writeOffset = this.positionOffset(entityId, segmentIndex + 1)
        this.positions[writeOffset] = Math.fround(nextX + (currentX - nextX) * t)
        this.positions[writeOffset + 1] = Math.fround(nextZ + (currentZ - nextZ) * t)
        segmentIndex++
      }

      distanceFromHead = nextDistanceFromHead
      currentPathIndex = previousPathIndex
      steps++
    }

    if (segmentIndex < count - 1) {
      const oldestIndex = (this.pathHead[entityId] - (availablePoints - 1) + this.pathCapacity) % this.pathCapacity
      const oldestOffset = this.pathOffset(entityId, oldestIndex)
      const oldestX = this.pathX[oldestOffset]
      const oldestZ = this.pathZ[oldestOffset]
      for (let i = segmentIndex + 1; i < count; i++) {
        const writeOffset = this.positionOffset(entityId, i)
        this.positions[writeOffset] = oldestX
        this.positions[writeOffset + 1] = oldestZ
      }
    }
  }

  setCountFromLength(entityId: number, length: number): void {
    const nextCount = Math.max(1, Math.min(Math.floor(length), this.maxSegmentsPerSnake))
    const currentCount = this.counts[entityId]

    if (nextCount > currentCount) {
      const tailIndex = Math.max(0, currentCount - 1)
      const tailOffset = this.positionOffset(entityId, tailIndex)
      const tailX = this.positions[tailOffset]
      const tailZ = this.positions[tailOffset + 1]
      for (let segmentIndex = currentCount; segmentIndex < nextCount; segmentIndex++) {
        const offset = this.positionOffset(entityId, segmentIndex)
        this.positions[offset] = tailX
        this.positions[offset + 1] = tailZ
      }
    }

    this.counts[entityId] = nextCount
  }

  trimTail(entityId: number, removeCount: number): number {
    let removed = 0
    while (removed < removeCount && this.counts[entityId] > 1) {
      this.counts[entityId]--
      removed++
    }
    return removed
  }

  snapshot(entityId: number): ChainPointV4[] {
    const count = this.counts[entityId]
    const points: ChainPointV4[] = []
    for (let i = 0; i < count; i++) {
      const offset = this.positionOffset(entityId, i)
      points.push({ x: this.positions[offset], z: this.positions[offset + 1] })
    }
    return points
  }
}
